require("../assets/css/font.css");
import { Chart } from "chart.js/auto";

/**
 * Converte un colore CSS in una versione trasparente (con opacità 0.6).
 * @param {string} colorName - Nome del colore (es. "red", "#ff5733", "rgb(255, 0, 0)").
 * @returns {string} Il colore in formato RGBA con opacità 0.6, oppure il valore originale se non valido.
 */
function transparentColor(colorName) {
    if (!colorName) return colorName;

    // Crea un elemento temporaneo per calcolare il colore reale in formato RGB
    const element = document.createElement('div');
    element.style.color = colorName;
    document.body.appendChild(element); // Aggiungi l'elemento al DOM (necessario per calcolare lo stile)

    const computedColor = globalThis.getComputedStyle(element).color;
    element.remove();

    const rgbValues = computedColor.match(/\d+/g);
    if (!rgbValues || rgbValues.length < 3) return colorName;

    return `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, 0.6)`;
}

/**
 * Extended color palette with 60 visually distinct colors.
 * Designed so that even with many datasets, no color repeats.
 */
const DISTINCT_COLORS = [
    '#e6194b', '#3cb44b', '#4363d8', '#f58231', '#911eb4',
    '#42d4f4', '#f032e6', '#bfef45', '#fabed4', '#469990',
    '#dcbeff', '#9a6324', '#fffac8', '#800000', '#aaffc3',
    '#808000', '#ffd8b1', '#000075', '#a9a9a9', '#e6beff',
    '#1abc9c', '#2ecc71', '#3498db', '#9b59b6', '#34495e',
    '#f39c12', '#d35400', '#c0392b', '#7f8c8d', '#27ae60',
    '#2980b9', '#8e44ad', '#2c3e50', '#f1c40f', '#e74c3c',
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
    '#393b79', '#637939', '#8c6d31', '#843c39', '#7b4173',
    '#5254a3', '#8ca252', '#bd9e39', '#ad494a', '#a55194',
    '#6b6ecf', '#b5cf6b', '#e7ba52', '#d6616b', '#ce6dbd',
];

/**
 * Returns a unique color for dataset at given index.
 * If index exceeds palette size, generates a deterministic HSL color.
 */
function getDatasetColor(index) {
    if (index < DISTINCT_COLORS.length) {
        return DISTINCT_COLORS[index];
    }
    // Fallback: generate evenly-spaced hues for large datasets
    const hue = (index * 137.508) % 360; // golden angle approximation
    return `hsl(${Math.round(hue)}, 65%, 50%)`;
}

function getDatasetColorTransparent(index) {
    const color = getDatasetColor(index);
    if (color.startsWith('hsl')) {
        return color.replace('50%)', '50%, 0.6)').replace('hsl(', 'hsla(');
    }
    // Convert hex to rgba
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.6)`;
}
class LaneAnalyticsTransitChart extends HTMLElement {
    static get observedAttributes() {
        return ["render", "mode", "min-width", "min-height"];
    }

    constructor() {
        super();
        this._shadowRoot = this.attachShadow({ mode: "open" });
        let styleSheet = document.createElement("link");
        styleSheet.setAttribute("rel", "stylesheet");
        styleSheet.setAttribute("type", "text/css");
        styleSheet.setAttribute("href", "/analytics/1.0/res/css/style.css");
        let fontFile = document.createElement("link");
        fontFile.setAttribute("rel", "stylesheet");
        fontFile.setAttribute("type", "text/css");
        fontFile.setAttribute("href", "/analytics/1.0/res/css/font.css");
        this._shadowRoot.appendChild(styleSheet)
        this._shadowRoot.appendChild(fontFile)
        const biggerDiv = document.createElement("div");
        biggerDiv.id = "analytics-container";
        const topDiv = document.createElement("div");
        topDiv.id = "top-container";
        let titleDiv = document.createElement("div");
        titleDiv.id = "title-div";
        topDiv.appendChild(titleDiv);
        let subtitleDiv = document.createElement("div");
        subtitleDiv.id = "subtitle-div";
        topDiv.appendChild(subtitleDiv);
        let metricsDiv = document.createElement("div");
        metricsDiv.id = "metrics-center-div";
        topDiv.appendChild(metricsDiv);
        biggerDiv.appendChild(topDiv);
        this._shadowRoot.appendChild(biggerDiv);
        this.chart = null;
    }

    disconnectedCallback() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === "render") {
            setTimeout(() => this.featuresInitializer().then(() =>{}), 0);
        }
        if (name === "min-width" && newValue) {
            this.style.minWidth = newValue;
        }
        if (name === "min-height" && newValue) {
            const chartDiv = this._shadowRoot.querySelector("#graph-container");
            if (chartDiv) chartDiv.style.minHeight = newValue;
            this.style.minHeight = newValue;
        }
    }

    async loadTranslation() {
        try {
            let suppLangs = ["it", "fr", "en", "de", "ja", "es", "zh"];
            let language = this.attributes?.language?.value?.toLowerCase() || "it";
            if (!suppLangs.includes(language))
                language = "it";

            if (this._translationCache) {
                return this._translationCache;
            }
            const response = await fetch(`/analytics/1.0/res/translations/${language}.json`);
            if (response.ok) {
                let result = await response.json();
                this._translationCache = result;
                return result;
            }
        } catch (error) {
            console.error("Errore durante il caricamento del labelMap:", error);
        }
    }

    async loadConfig() {
        try {
            if (sessionStorage.getItem("analytics-config")) {
                return JSON.parse(sessionStorage.getItem("analytics-config"));
            }
            const response = await fetch("/analytics/1.0/api/config");
            if (response.ok) {
                let result = await response.json();
                sessionStorage.setItem("analytics-config", JSON.stringify(result));
                return result;
            }
        } catch (error) {
            console.warn("lane-analytics: could not load config", error);
        }
        return {};
    }

    generateLabels(datasetsObj) {
        let labels =[];
        let sortkeys = [];
        for (const item in datasetsObj) {
            datasetsObj[item].forEach((dataItem) => {
                if (!labels.includes(dataItem.label)) {
                    labels.push(dataItem.label);
                    sortkeys.push(dataItem.sortkey);
                }
            });
        }

        let sortedIndices = sortkeys
        .map((sortkey, index) => ({ sortkey, index }))
        .sort((a, b) => a.sortkey.toLowerCase().localeCompare(b.sortkey.toLowerCase()));
        labels = sortedIndices.map(item => labels[item.index]);
        
        return labels;
    }

    /**
     * For trend mode: builds an ordered list of {label, sortkey} pairs
     * preserving every occurrence (labels repeat per time unit).
     * Returns the union of all time points in chronological order.
     */
    generateTrendLabels(datasetsObj) {
        // Collect all unique sortkeys across all datasets
        const sortkeySet = new Map(); // sortkey → display label
        for (const item in datasetsObj) {
            datasetsObj[item].forEach((dataItem) => {
                if (!sortkeySet.has(dataItem.sortkey)) {
                    sortkeySet.set(dataItem.sortkey, dataItem.label);
                }
            });
        }
        // Sort by sortkey
        const sorted = [...sortkeySet.entries()]
            .sort((a, b) => a[0].toLowerCase().localeCompare(b[0].toLowerCase()));
        return sorted; // array of [sortkey, label]
    }


    generateChartData = async (apiResponse) => {
        const mode = this._resolvedMode();

        if (mode === "trend") {
            return await this._generateTrendChartData(apiResponse);
        }
        return await this._generateDistributionChartData(apiResponse);
    };

    _generateDistributionChartData = async (apiResponse) => {
        const labels = this.generateLabels(apiResponse);
        const datasets = [];
        let labelMap = {};
        let translation = await this.loadTranslation();
        labelMap = translation.labelMap
        let i = 0;
        let baseColor = this.getAttribute("color") || '';
        let attrDataset = this.getAttribute("datasets") || null;
        let keys = Object.keys(apiResponse);
        switch (attrDataset) {
            case "place":
            case "lane":
            case "tenant":
            case "vehicle_class":
            case "direction":
                keys = keys.sort((a, b) => a.localeCompare(b));
                break;
        }
        for (const item of keys) {
            if (apiResponse.hasOwnProperty(item)) {
                let itemKey = attrDataset === "vehicle_class" ? `VC_${item}` : item;
                const dataset = {
                    label: labelMap[itemKey] || translation[itemKey] || item,
                    data: labels.map((label) => {
                        let matchingItem = {};
                        matchingItem.value = 0;
                        matchingItem = apiResponse[item].find(
                            (dataItem) => dataItem.label === label
                        );
                        if (!matchingItem) {
                            matchingItem = {};
                            matchingItem.value = 0;
                        }

                        return matchingItem ? matchingItem.value : 0;
                    }),
                    borderRadius: 6,
                    lineTension: 0.2,
                    backgroundColor: transparentColor(baseColor),
                    borderColor: baseColor

                };
                datasets.push(dataset);
                i++;
            }
        }

        let spanAttr = this.getAttribute("span") || null;
        let translatedLabels = labels.map((label) => {
            let labelKey = spanAttr === "vehicle_class" ? `VC_${label}` : label;
            return labelMap[labelKey] || translation[labelKey] || label;
        });

        if (!this.attributes.last24h &&
            (this.attributes?.period?.value == 'hourly' ||
                this.attributes.graph?.value == "transitweek")) {

            let sortedData = translatedLabels.map((label, index) => ({
                label: label,
                data: datasets.map(dataset => dataset.data[index])
            }));

            sortedData.sort((a, b) => a.label.localeCompare(b.label));

            translatedLabels = sortedData.map(item => item.label);
            datasets.forEach((dataset) => {
                dataset.data = sortedData.map(item => item.data[datasets.indexOf(dataset)]);
            });
        }

        return {
            labels: translatedLabels,
            datasets,
        };
    };

    /**
     * Generates chart data for "trend" mode.
     * X-axis points are ordered chronologically; the span criterion (e.g. hourly slot)
     * repeats for each day in the date range so you can see the trend over time.
     */
    _generateTrendChartData = async (apiResponse) => {
        const translation = await this.loadTranslation();
        const labelMap = translation.labelMap || {};
        const baseColor = this.getAttribute("color") || '';
        const attrDataset = this.getAttribute("datasets") || null;

        // Build the ordered list of all time points (sortkey → display label)
        const orderedPoints = this.generateTrendLabels(apiResponse); // [[sortkey, rawLabel], ...]

        let keys = Object.keys(apiResponse);
        if (attrDataset) {
            switch (attrDataset) {
                case "place":
                case "lane":
                case "tenant":
                case "vehicle_class":
                case "direction":
                    keys = keys.sort((a, b) => a.localeCompare(b));
                    break;
            }
        }

        const datasets = [];
        for (const item of keys) {
            if (!apiResponse.hasOwnProperty(item)) continue;
            let itemKey = attrDataset === "vehicle_class" ? `VC_${item}` : item;
            const dataset = {
                label: labelMap[itemKey] || translation[itemKey] || item,
                data: orderedPoints.map(([sortkey]) => {
                    const match = apiResponse[item].find(d => d.sortkey === sortkey);
                    return match ? match.value : 0;
                }),
                borderRadius: 6,
                lineTension: 0.4,
                tension: 0.4,
                fill: false,
                backgroundColor: transparentColor(baseColor),
                borderColor: baseColor,
                pointRadius: orderedPoints.length > 100 ? 0 : 3,
            };
            datasets.push(dataset);
        }

        // Translate the display labels (the repeating criterion labels, e.g. "h08")
        // Encode as "translatedLabel;datePart" only for sub-daily spans (hour, quarterly)
        // where the primary label is a time and the date adds context.
        // For daily or coarser spans the label already represents the date, no dual axis needed.
        const spanAttr = this.getAttribute("span") || null;
        const subDailySpans = ['hour', 'quarterly', 'day'];
        const needsDualAxis = subDailySpans.includes(spanAttr);
        const sortkeys = orderedPoints.map(([sortkey]) => sortkey);
        const translatedLabels = orderedPoints.map(([sortkey, rawLabel]) => {
            let labelKey = spanAttr === "vehicle_class" ? `VC_${rawLabel}` : rawLabel;
            const translated = labelMap[labelKey] || translation[labelKey] || rawLabel;

            if (needsDualAxis) {
                // Extract date portion from sortkey (format: "YYYY/MM/DD/...")
                const datePart = sortkey.substring(0, 10); // "YYYY/MM/DD"
                if (datePart.includes("/")) {
                    const parts = datePart.split("/");
                    if (parts.length >= 3) {
                        return `${translated};${parts[2]}/${parts[1]}`;
                    }
                }
                return `${translated};`;
            }
            return translated;
        });

        return { labels: translatedLabels, datasets, trend: true, sortkeys, needsDualAxis };
    };

    //titolo valorizzato solo se esiste il grafico

    async renderGraph() {
        try {
            const apiURL = this.buildApiUrl();
            if (!apiURL) return;

            let chartOptions = await this.buildChartOptions(apiURL);
            this.title(chartOptions.title.text);
            this.subTitle(chartOptions.subtitle.text);

            const res = await fetch(apiURL);
            if (!res.ok) {
                throw new Error(`Error: ${res.statusText}`);
            }

            const data = await res.json();

            const chartData = await this.generateChartData(data);
            const context = this._shadowRoot.querySelector("canvas").getContext("2d");


            this.renderChart(context, chartData, chartOptions);


            if (this.hasAttribute("obfuscate")) {
                this.clearAttributes();
            }
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * Returns the resolved component mode: "distribution", "trend", or null (legacy).
     * "distribution" → always bar chart
     * "trend"        → always line chart, X-axis criteria repeated over time intervals
     */
    _resolvedMode() {
        const raw = (this.getAttribute("mode") || "").toLowerCase().trim();
        if (raw === "distribution") return "distribution";
        if (raw === "trend") return "trend";
        return null;
    }

    buildApiUrl() {
        let apiURL = "/analytics/1.0/api/graph?";
        const params = new URLSearchParams();

        this.addDateFilters(params);
        this.addGraphFilters(params);
        this.addCustomAttributes(params);

        // trend mode forces line chart and requests chronological time-series data
        const mode = this._resolvedMode();
        if (mode === "trend") {
            params.set("trend", "true");
        }

        apiURL += params.toString();
        return apiURL.includes("{") || apiURL.includes("}") ? null : apiURL;
    }

    buildMetricsUrl() {
        let apiURL = "/analytics/1.0/api/metrics?";
        const params = new URLSearchParams();

        this.addDateFilters(params);
        this.addGraphFilters(params);
        this.addCustomAttributes(params);

        apiURL += params.toString();
        return apiURL.includes("{") || apiURL.includes("}") ? null : apiURL;
    }

    addDateFilters(params) {
        const dateRanges = {
            last24h: 24,
            lastWeek: 7,
            last30gg: 30,
        };
        Array.from(this.attributes).forEach((attr) => {
            if (attr.nodeName.includes('ng-'))
                return;
            switch (attr.nodeName) {
                case "begin-date":
                    params.set("begin_date", `gte.${attr.value}`);
                    break;
                case "end-date":
                    params.set("end_date", `lt.${attr.value}`);
                    break;
            }
        });

        if (this.hasAttribute("begin-date") && this.hasAttribute("end-date")) {
            const beginDate = new Date(this.getAttribute("begin-date"));
            const endDate = new Date(this.getAttribute("end-date"));
            if (beginDate > endDate) {
                console.error("Invalid date range: begin-date is after end-date.");
                return;
            }
            params.set("begin_date", `gte.${beginDate.toISOString()}`);

            params.set("end_date", `lt.${endDate.toISOString()}`);
        }
        
        if (this.hasAttribute("today")) {
            const endDate = new Date();
            endDate.setDate(endDate.getDate() + 1);
            params.set("end_date", `lte.${endDate.toISOString()}`);

            const startDate = new Date();
            startDate.setHours(1, 0, 0);
            params.set("begin_date", `gte.${startDate.toISOString()}`);
        } else {
            for (const [key, hoursOrDays] of Object.entries(dateRanges)) {
                if (this.hasAttribute(key)) {
                    const endDate = new Date();
                    params.set("end_date", `lt.${endDate.toISOString()}`);

                    const startDate = new Date();
                    if (key === "last24h") {
                        startDate.setHours(startDate.getHours() - hoursOrDays);
                    } else {
                        startDate.setDate(startDate.getDate() - hoursOrDays);
                    }
                    params.set("begin_date", `gte.${startDate.toISOString()}`);
                    break;
                }
            }
        }
    }

    addGraphFilters(params) {
        if (this.hasAttribute("graph")) {
            const graphType = this.getAttribute("graph").toLowerCase();
            const newDate = new Date();

            const graphFilters = {
                transitweek: () => {
                    params.set("end_date", `lt.${newDate.toISOString()}`);
                    newDate.setDate(newDate.getDate() - 7);
                    params.set("begin_date", `gte.${newDate.toISOString()}`);
                    params.set("span", "hour");
                },
                transitmonth: () => {
                    params.set("end_date", `lt.${newDate.toISOString()}`);
                    newDate.setDate(newDate.getDate() - 30);
                    params.set("begin_date", `gte.${newDate.toISOString()}`);
                    params.set("span", "daymonth");
                },
                transittime: () => {
                    params.set("type", "duration");
                    params.set("end_date", `lt.${newDate.toISOString()}`);
                    this.setAttribute("type", "duration")
                    newDate.setDate(newDate.getDate() - 30);
                    params.set("begin_date", `gte.${newDate.toISOString()}`);
                    params.set("span", "daymonth");
                },
                transitday: () => {
                    params.set("end_date", `lt.${newDate.toISOString()}`);
                    newDate.setDate(newDate.getDate() - 30);
                    params.set("begin_date", `gte.${newDate.toISOString()}`);
                    params.set("span", "day");
                },
                transitquarterly: () => {
                    params.set("end_date", `lt.${newDate.toISOString()}`);
                    newDate.setHours(newDate.getHours() - 24);
                    params.set("begin_date", `gte.${newDate.toISOString()}`);
                    params.set("span", "quarterly");
                },
            };

            if (graphFilters[graphType]) {
                graphFilters[graphType]();
            }
        }
    }

    addCustomAttributes(params) {
        Array.from(this.attributes).forEach((attr) => {
            const IGNORE_ATTRIBUTES = ["color", "graph", "chart", "today", "last24h", "lastWeek", "last30gg", "id", "download", "download-csv", "graph-subtitle", "graph-title", 
                                       "title-align", "title-position", "title-weight", "title-size", "obfuscate", "span-dropdown", "metrics", "end-date", "begin-date", "filters", "mode"];
            if (!attr.nodeName.toLowerCase().includes("ng-") && !IGNORE_ATTRIBUTES.includes(attr.nodeName.toLowerCase())) {
                params.set(attr.nodeName, attr.value);
            }
        });

        // Parse JSON filters attribute
        if (this.hasAttribute("filters")) {
            try {
                const filters = JSON.parse(this.getAttribute("filters"));
                if (filters && typeof filters === "object") {
                    Object.entries(filters).forEach(([key, value]) => {
                        params.set(key, value);
                    });
                }
            } catch (e) {
                console.warn("lane-analytics: invalid filters JSON", e);
            }
        }
    }

    async buildChartOptions(apiUrl) {
        const mode = this._resolvedMode();

        // Determine chart type: mode takes priority over chart-type attribute
        let resolvedChartType;
        if (mode === "distribution") {
            resolvedChartType = "bar";
        } else if (mode === "trend") {
            resolvedChartType = "line";
        } else {
            resolvedChartType = this.getAttribute("chart-type") || "bar";
        }

        let title = ''
        if(this.hasAttribute("graph-title")){
            title = this.getAttribute("graph-title");
        }
        else{
            let translation = await this.loadTranslation();
            title = 'Lane analytics';
            if (this.hasAttribute("graph")) {
                title = translation[this.getAttribute("graph")] || 'Lane analytics';
            } else if (mode || this.hasAttribute("chart-type")) {
                // Derive label key from mode or chart-type attribute
                let chartTypeLabel;
                if (mode === "distribution") {
                    chartTypeLabel = "distribution";
                } else if (mode === "trend") {
                    chartTypeLabel = "trend";
                } else {
                    chartTypeLabel = this.getAttribute("chart-type");
                    if (chartTypeLabel == 'line') {
                        chartTypeLabel = 'trend';
                    } else if (chartTypeLabel == 'bar') {
                        chartTypeLabel = 'distribution';
                    }
                }
                let isDuration = this.hasAttribute("type") && this.getAttribute('type') == 'duration';

                if (isDuration) {
                    title = `${translation[chartTypeLabel] || chartTypeLabel} ${translation['duration'] || 'duration'} ${translation['of'] || 'of'}`;
                } else {
                    title = `${translation[chartTypeLabel] || chartTypeLabel} ${translation['of'] || 'of'}`;
                }

                title = `${title} ${translation['transits'] || 'transits'} ${translation['for'] || 'for'} ${ translation[this.getAttribute("span")] || this.getAttribute("span") || ''}`;
            }

        }
        
        let subtitle = '';
        if(this.hasAttribute("graph-subtitle")){
            subtitle = this.getAttribute("graph-subtitle");
        }else{
            let translation = await this.loadTranslation();
            subtitle = 'Lane analytics';
            if (apiUrl.includes("begin_date") && apiUrl.includes("end_date")) {
                const urlParams = new URLSearchParams(apiUrl.split("?")[1]);
                const beginDate = urlParams.get("begin_date").split(".")[1];
                const endDate = urlParams.get("end_date").split(".")[1];
                
                let formattedBeginDate= '';
                let formattedEndDate= '';

                const objectBeginDate = new Date(beginDate + 'Z');
                const objEndDate = new Date(endDate + 'Z');
                if (objectBeginDate.getFullYear() == objEndDate.getFullYear() &&
                    objectBeginDate.getMonth() == objEndDate.getMonth() &&
                    objectBeginDate.getDate() == objEndDate.getDate()) {
                    formattedBeginDate = objectBeginDate.toLocaleString(navigator.language, {hour: '2-digit', minute: '2-digit'});
                    formattedEndDate = objEndDate.toLocaleString(navigator.language, {hour: '2-digit', minute: '2-digit'});
                    let formattedDay = objectBeginDate.toLocaleString(navigator.language, { day: '2-digit', month: '2-digit', year: 'numeric' });
                    subtitle = `${translation['from_hour'] || 'From'} ${formattedBeginDate} ${translation['to_hour'] || 'to'} ${formattedEndDate} ${translation['On'] || 'on'} ${formattedDay}`;
                }else{
                    formattedBeginDate = objectBeginDate.toLocaleString(navigator.language, { day: '2-digit', month: '2-digit', year: 'numeric' });
                    formattedEndDate = objEndDate.toLocaleString(navigator.language, { day: '2-digit', month: '2-digit', year: 'numeric' });
                    subtitle = `${translation['from'] || 'From'} ${formattedBeginDate} ${translation['to'] || 'to'} ${formattedEndDate}`;
                }
                
            }
        }
        return {
            type: resolvedChartType,
            indexAxis: "x",
            title: {
                text: title.charAt(0).toUpperCase() + title.toLowerCase().slice(1),
            },
            subtitle: {
                text: subtitle.charAt(0).toUpperCase() + subtitle.toLowerCase().slice(1),
            }
        };
    }

    clearAttributes() {
        [...this.attributes].forEach((attr) => this.removeAttribute(attr.name));
    }

    downloadFunction(){
        let a = document.createElement('a');
        a.href = this.chart.toBase64Image();
        a.download = 'chart.png';

		if(this.hasAttribute('graph-title')){
			a.download = this.getAttribute('graph-title') + '.png';
		}

        a.click();
        a.remove();
    }

    /**
     * Converts a sortkey from the DB (e.g. "2026/06/08/h14", "2026/06/08/14:15",
     * "2026/06/08", "2026/06", "2026/24", "2026") into an Excel-compatible date string.
     * Output format: DD/MM/YYYY HH:mm (or DD/MM/YYYY, MM/YYYY, YYYY depending on granularity).
     */
    _sortkeyToCsvDate(sortkey) {
        if (!sortkey) return '';
        const parts = sortkey.split('/');

        // "YYYY" only (year span)
        if (parts.length === 1) return parts[0];

        // "YYYY/MM" (month span) or "YYYY/IW" (week span)
        if (parts.length === 2) {
            const [year, second] = parts;
            // Week format: IW is 1-53
            if (second.length <= 2 && Number(second) >= 1 && Number(second) <= 53) {
                // Could be month (01-12) or week — if > 12 it's definitely a week
                if (Number(second) > 12) return `${year} W${second}`;
                // Assume month for values 01-12
                return `${second}/${year}`;
            }
            return `${second}/${year}`;
        }

        // "YYYY/MM/DD" or "YYYY/MM/DD/time"
        const [year, month, day, ...timeParts] = parts;
        const datePart = `${day}/${month}/${year}`;

        if (timeParts.length === 0) return datePart;

        // Time part: "hHH" (hour span) or "HH:MM" (quarterly span)
        const timeStr = timeParts.join('/');
        const hourMatch = timeStr.match(/^h(\d{2})$/i);
        if (hourMatch) {
            return `${datePart} ${hourMatch[1]}:00`;
        }
        // "HH:MM" format
        const timeMatch = timeStr.match(/^(\d{2}):(\d{2})$/);
        if (timeMatch) {
            return `${datePart} ${timeMatch[1]}:${timeMatch[2]}`;
        }

        // Fallback
        return `${datePart} ${timeStr}`;
    }

    downloadCsvFunction(){
        if (!this.chart) return;

        const chartData = this.chart.data;
        const labels = chartData.labels || [];
        const allDatasets = chartData.datasets || [];

        // Only include datasets that are currently visible in the chart
        const datasets = allDatasets.filter((ds, index) => {
            const meta = this.chart.getDatasetMeta(index);
            return !meta.hidden;
        });

        const separator = this._csvSeparator || ";";
        const rows = [];

        // Header row: first column is the label axis, then one column per visible dataset
        const header = [""].concat(datasets.map(ds => ds.label || ""));
        rows.push(header.join(separator));

        // Data rows: one per label
        const sortkeys = chartData.sortkeys || null;
        labels.forEach((label, index) => {
            let csvLabel;
            if (sortkeys && sortkeys[index]) {
                // Build an Excel-compatible date from the sortkey (YYYY/MM/DD/time)
                csvLabel = this._sortkeyToCsvDate(sortkeys[index]);
            } else if (typeof label === 'string' && label.includes(';')) {
                // Fallback: strip internal separator
                const parts = label.split(';');
                csvLabel = parts[1] ? `${parts[0]} ${parts[1]}` : parts[0];
            } else if (Array.isArray(label)) {
                csvLabel = label.join(' ');
            } else {
                csvLabel = label;
            }
            const row = [csvLabel].concat(datasets.map(ds => {
                const value = ds.data[index] != null ? ds.data[index] : "";
                return value;
            }));
            rows.push(row.join(separator));
        });

        const csvContent = rows.join("\n");
        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        let a = document.createElement("a");
        a.href = url;
        a.download = "chart.csv";
        if (this.hasAttribute("graph-title")) {
            a.download = this.getAttribute("graph-title") + ".csv";
        }
        a.click();
        URL.revokeObjectURL(url);
        a.remove();
    }

    dropdownRefreshGraph(){
        let dropdown = this._shadowRoot.querySelector("select[id*='span-dropdown']");
        this.setAttribute("span", dropdown.value);
        this.renderGraph();
    }

    /**
    * Initializes a dropdown element with the specified options and styles, and appends it to the shadow DOM.
    * 
    * @param {HTMLSelectElement} dropdown - The dropdown element to initialize.
    * @param {string} defaultValue - The default selected value for the dropdown.
    * @param {string} id - The ID to assign to the dropdown element.
    * @param {string[]} options - An array of option values to populate the dropdown.
    * @param {string} borderColor - The border color of the dropdown.
    * @param {string} textColor - The text color of the dropdown.
    * @param {string} backgroundColor - The background color of the dropdown.
    * @param {string} focusBorderColor - The border color when the dropdown is focused.
    * @param {string} focusShadowColor - The shadow color when the dropdown is focused.
    * @param {Function} onChangeFunc - The callback function to execute when the dropdown value changes.
    * @returns {Promise<void>} A promise that resolves when the dropdown is fully initialized.
    */
    async initializeDropdown(dropdown,defaultValue, id, options, borderColor, textColor, backgroundColor, focusBorderColor,focusShadowColor, onChangeFunc){
        if(this.hasAttribute('id')){
            dropdown.id = this.attributes.id.value + '-' + id;
        }else{
            dropdown.id = id;
        }
        dropdown.classList.add("dropDown");
        dropdown.style=`
            background: ${backgroundColor};
            border: 1px solid ${borderColor};
            color: ${textColor};
        `;
        dropdown.addEventListener("focus", () => {
            dropdown.style.border = `1px solid ${focusBorderColor}`;
            dropdown.style.boxShadow = `0 0 0 0.2rem ${focusShadowColor}`;
        });
        dropdown.addEventListener("blur", () => {
            dropdown.style.border = `1px solid ${borderColor}`;
            dropdown.style.boxShadow = "none";
        });
        dropdown.innerHTML = "";
        let translation = await this.loadTranslation();
        
        options.forEach((optionValue) => {
            let option = document.createElement("option");
            option.value = optionValue;
            option.textContent = translation[optionValue] || optionValue.charAt(0).toUpperCase() + optionValue.slice(1);
            dropdown.appendChild(option);
        });

        dropdown.value = defaultValue;

        let onChange = onChangeFunc.bind(this);
        dropdown.addEventListener("change", onChange);
    }

    /**
     * Initializes a button element with the specified styles, text, and click callback function.
     *
     * @param {HTMLButtonElement} btn - The button element to initialize.
     * @param {string} backgroundColor - The background color of the button.
     * @param {string} borderColor - The border color of the button.
     * @param {string} mouseoverColor - The background color of the button when hovered.
     * @param {string} textColor - The text color of the button.
     * @param {string} text - The text to display on the button.
     * @param {Function} callbackFunction - The callback function to execute when the button is clicked.
     * @param {string} iconClass - The icon string to append to the button.
     */
    initializeBtn(btn, backgroundColor, borderColor, mouseoverColor, textColor, text, callbackFunction, iconClass){
        btn.classList.add("btn-default");
        btn.style = `
                    background-color: ${backgroundColor}; 
                    color: ${textColor};
                    border: 1px solid ${borderColor};
                `;
        btn.addEventListener("mouseover", function(){
            this.style.backgroundColor = mouseoverColor;
        });
        btn.addEventListener("mouseout", function(){
            this.style.backgroundColor = backgroundColor;
        });

        let div = document.createElement("div");
        div.classList.add("div-just-center");
        btn.appendChild(div);
        
        let icon = document.createElement("i");
        icon.name = "icon";
        icon.className = "icon-" + iconClass;
        icon.style.fontSize = "18px";
        icon.style.color = textColor;
        div.appendChild(icon);

        let span = document.createElement("span");
        span.textContent = text;
        span.className = "padding-0-0-0-10";
        
        div.appendChild(span);

        let callback = callbackFunction.bind(this);
        btn.addEventListener("click", callback);
    }

    async featuresInitializer() {
        const analyticsConfig = await this.loadConfig();
        this._csvSeparator = analyticsConfig.csvSeparator || ";";

        if(this.hasAttribute("chart")){
            let biggerDiv = this._shadowRoot.querySelector("div[id*='analytics-container']");
            biggerDiv.className = "analytics-container";
            let graphDiv = this._shadowRoot.querySelector("div[id*='graph-container']");
            if(!graphDiv){
                graphDiv = document.createElement("div");
                graphDiv.id = 'graph-container';
                graphDiv.className = "chart-div";
                const canvas = document.createElement("canvas");
                graphDiv.appendChild(canvas);
                biggerDiv.appendChild(graphDiv);
            }

            // Apply min-height to the chart container if attribute is set
            if (this.hasAttribute("min-height")) {
                graphDiv.style.minHeight = this.getAttribute("min-height");
                graphDiv.style.height = "auto";
            }


            let controlsDiv = this._shadowRoot.querySelector("div[id*='controls-container']");
            if(!controlsDiv){
                controlsDiv = document.createElement("div");
                controlsDiv.id = "controls-container";
                controlsDiv.className = "div-just-space-bet";
                this._shadowRoot.appendChild(controlsDiv);
            }

            let dropdownDiv = controlsDiv.querySelector("div[id*='dropdown-div']");
            if(!dropdownDiv){
                dropdownDiv = document.createElement("div");
                dropdownDiv.id = "dropdown-div";
                dropdownDiv.className = "div-just-space-bet";
                controlsDiv.appendChild(dropdownDiv);
            }

            let buttonsDiv = controlsDiv.querySelector("div[id*='buttons-div']");
            if(!buttonsDiv){
                buttonsDiv = document.createElement("div");
                buttonsDiv.id = "buttons-div";
                buttonsDiv.className = "div-just-space-bet";
                controlsDiv.appendChild(buttonsDiv);
            }

            if(this.hasAttribute("download")){
                let btn = this._shadowRoot.querySelector("button");
                if(!btn){
                    btn = document.createElement("button");
                    btn.id = "download";
                    if(this.hasAttribute('id')){
                        btn.id = this.attributes.id.value + '-download-png';
                    }
                    this.initializeBtn(btn, "#337ab7", "#2e6da4", "#0056b3", "white", "PNG", this.downloadFunction, "image");
                    buttonsDiv.appendChild(btn);
                }
            }

            if(this.hasAttribute("download-csv")){
                let csvBtn = this._shadowRoot.querySelector("button[id*='download-csv']");
                if(!csvBtn){
                    csvBtn = document.createElement("button");
                    csvBtn.id = "download-csv";
                    if(this.hasAttribute('id')){
                        csvBtn.id = this.attributes.id.value + '-download-csv';
                    }

                    // Parse appearance from attribute value (format: "bgColor,borderColor,hoverColor,textColor")
                    let csvAttr = this.getAttribute("download-csv") || "";
                    let csvColors = csvAttr.split(",").map(c => c.trim()).filter(c => c);
                    let bgColor = csvColors[0] || "#337ab7";
                    let borderColor = csvColors[1] || "#2e6da4";
                    let hoverColor = csvColors[2] || "#0056b3";
                    let textColor = csvColors[3] || "white";

                    this.initializeBtn(csvBtn, bgColor, borderColor, hoverColor, textColor, "CSV", this.downloadCsvFunction, "download");
                    buttonsDiv.appendChild(csvBtn);
                }
            }

            if (this.hasAttribute("span-dropdown")){
                let dropdown = this._shadowRoot.querySelector("select[id*='span-dropdown']");
                if(!dropdown){
                    let div = document.createElement("div");
                    div.id = "span-dropdown-div";
                    dropdown = document.createElement("select");
                    let label = document.createElement("label");
                    label.setAttribute("for", "span-dropdown");
                    if(this.hasAttribute('id')){
                        label.setAttribute("for", this.attributes.id.value + '-span-dropdown');
                    }
                    let translatedLabels = await this.loadTranslation();
                    label.textContent = translatedLabels['Show data by'];
                    label.className = "dropDown-label";
                    label.style.paddingRight = "10px";

                    const mode = this._resolvedMode();
                    let options;
                    if (mode === "trend") {
                        // Trend only makes sense for temporal spans
                        options = ["quarterly", "hour", "day", "daymonth", "week", "month", "year"];
                    } else {
                        options = ["quarterly", "hour", /*"last24h",*/ "day", "daymonth", "week", "month", "year", "place", "lane", "vehicle_class", "direction"];
                    }

                    await this.initializeDropdown(dropdown, this.getAttribute("span") || "daymonth", "span-dropdown",
                        options, "#ced4da", "#495057", "#fff", "#007bff", "rgba(0, 123, 255, 0.25)",
                        this.dropdownRefreshGraph
                        );
                    div.appendChild(label);
                    div.appendChild(dropdown);
                    dropdownDiv.appendChild(div);
                }
            }

            await this.renderGraph();
        }

        if(this.hasAttribute("metrics")){
            const apiURL = this.buildMetricsUrl();
            if (!apiURL) return;

            const res = await fetch(apiURL);
            if (!res.ok) {
                throw new Error(`Error: ${res.statusText}`);
            }

            const data = await res.json();
            let translation = await this.loadTranslation();
            this.metricsInitializer(data.actual, data.trend, data.previous ?? 0, translation);
        }
    }



    renderChart(context, chartData, chartOptions) {

        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
        if (chartData.datasets.length > 1) {
            chartData.datasets.forEach((dataset, index) => {
                dataset.borderColor = getDatasetColor(index);
                dataset.backgroundColor = getDatasetColorTransparent(index);
                dataset.hoverBackgroundColor = getDatasetColor(index);
                dataset.hoverBorderColor = getDatasetColor(index);
            });
        } else {
            // Single dataset with externally defined color: set explicit hover colors
            // to avoid Chart.js auto-deriving inconsistent hover shades
            chartData.datasets.forEach((dataset) => {
                if (dataset.borderColor) {
                    dataset.hoverBackgroundColor = dataset.borderColor;
                    dataset.hoverBorderColor = dataset.borderColor;
                }
            });
        }

        // Build scales config — add secondary X axis for trend mode date labels (sub-daily spans only)
        const isTrend = chartData.trend === true;
        const needsDualAxis = chartData.needsDualAxis === true;
        const scales = {
            x: {
                type: 'category',
                ticks: {
                    callback: (value, index) => {
                        if (!needsDualAxis) return chartData.labels[index];
                        const label = chartData.labels[index] || '';
                        return label.split(';')[0];
                    },
                    maxRotation: isTrend ? 0 : undefined,
                    autoSkip: true,
                }
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: (this.attributes?.["type"] &&
                        this.attributes?.type?.value.toLowerCase() === 'duration') ?
                        (this._translationCache?.["Transits duration"] || "Duration (s)") :
                        (this._translationCache?.["Transits count"] || "Count"),
                    font: { size: 12, weight: '500' },
                    color: '#6c757d',
                },
                ticks: {
                    callback: (value, index, ticks) => {
                        return (this.attributes?.["type"] &&
                            this.attributes?.type?.value.toLowerCase() === 'duration') ?
                            `${value}s` : `${value}`;
                    }
                }
            }
        };

        if (needsDualAxis) {
            // Determine which tick indices are the midpoint of each day group
            const dayIndices = new Map(); // datePart → [indices]
            chartData.labels.forEach((lbl, i) => {
                const datePart = (lbl || '').split(';')[1] || '';
                if (datePart) {
                    if (!dayIndices.has(datePart)) dayIndices.set(datePart, []);
                    dayIndices.get(datePart).push(i);
                }
            });
            const showDateAtIndex = new Set();
            dayIndices.forEach((indices) => {
                const mid = indices[Math.floor(indices.length / 2)];
                showDateAtIndex.add(mid);
            });

            scales.x2 = {
                type: 'category',
                grid: { drawOnChartArea: false },
                ticks: {
                    callback: (value, index) => {
                        if (showDateAtIndex.has(index)) {
                            const label = chartData.labels[index] || '';
                            return label.split(';')[1] || '';
                        }
                        return '';
                    },
                    maxRotation: 0,
                    autoSkip: false,
                },
                border: { display: false },
            };

            // Assign all datasets to xAxis x (not x2)
            chartData.datasets.forEach(ds => { ds.xAxisID = 'x'; });
        }

        this.chart = new Chart(context, {
            type: chartOptions.type,
            options: {
                locale: "it-IT",
                indexAxis: chartOptions.indexAxis,
                elements: {
                    bar: {
                        borderWidth: 4,
                    },
                },
                plugins: {
                    colors: {
                        enabled: true,
                    },
                    legend: {
                        display: true,
                        position: 'top',
                        labels: {
                            boxWidth: 12,
                            font: { size: 11 },
                            padding: 8,
                        },
                        maxHeight: 80,
                        onClick: (e, legendItem, legend) => {
                            // Default Chart.js toggle behaviour
                            const index = legendItem.datasetIndex;
                            const chart = legend.chart;
                            if (chart.isDatasetVisible(index)) {
                                chart.hide(index);
                            } else {
                                chart.show(index);
                            }
                            // Notify outside listeners (e.g. the panel's checkbox filter)
                            // so they can mirror the visibility state in their UI.
                            const hiddenIndices = chart.data.datasets
                                .map((_, i) => ({ i, meta: chart.getDatasetMeta(i) }))
                                .filter(({ meta }) => !chart.isDatasetVisible(meta.index))
                                .map(({ i }) => i);
                            this.dispatchEvent(new CustomEvent("analytics-legend-click", {
                                bubbles: true,
                                composed: true,
                                detail: { hiddenIndices },
                            }));
                        },
                    },
                    tooltip: needsDualAxis ? {
                        callbacks: {
                            title: (items) => {
                                if (!items.length) return '';
                                const lbl = chartData.labels[items[0].dataIndex] || '';
                                const parts = lbl.split(';');
                                return parts[1] ? `${parts[0]} - ${parts[1]}` : parts[0];
                            }
                        }
                    } : {},
                },
                responsive: true,
                maintainAspectRatio: false,
                animation: {
                    duration: 800,
                    easing: 'easeInOutBack',
                },
                transitions: {
                    show: {
                        animations: {
                            y: {
                                from: (ctx) => ctx.chart.scales.y.bottom
                            },
                        },
                    },
                    hide: {
                        animations: {
                            y: {
                                to: (ctx) => ctx.chart.scales.y.bottom
                            },
                        },
                    },
                },
                scales: scales,
            },
            data: chartData,
        });
    }


    title(title){
        let analyticsContainer = this._shadowRoot.querySelector("div[id*='top-container']");
        let titleSpan = analyticsContainer.querySelector("span[id*='analytics-title']");
        let titleDiv = analyticsContainer.querySelector("div[id*='title-div']");
        if(!titleSpan){
            if(!titleDiv){
                titleDiv = document.createElement("div");
                titleDiv.id = "title-div";
            }
            titleSpan = document.createElement("span");
            titleSpan.id = "analytics-title";
            titleSpan.className = "title";
            titleDiv.appendChild(titleSpan);
            analyticsContainer.appendChild(titleDiv);
        }
        titleSpan.textContent = title;
        this.centerDivByTitle(titleDiv);
    }

    centerDivByTitle(div){
        div.className = "div-just-start";
        if(this.hasAttribute("title-align")){
            switch(this.getAttribute("title-align")){
                case 'center':
                    div.className = "div-just-center";
                    break;
                case 'start':
                case 'begin':
                case 'left':
                    div.className = "div-just-start";
                    break;
                case 'right':
                case 'end':
                case 'finish':
                    div.className = "div-just-end";
                    break;
            }
        }
    }

    subTitle(subtitle){
        let analyticsContainer = this._shadowRoot.querySelector("div[id*='top-container']");
        let subtitleSpan = analyticsContainer.querySelector("span[id*='analytics-subtitle']");
        let subtitleDiv = this._shadowRoot.querySelector("div[id*='subtitle-div']");
        if(!subtitleSpan){
            if(!subtitleDiv){
                subtitleDiv = document.createElement("div");
                subtitleDiv.id = "subtitle-div";
            }
            subtitleSpan = document.createElement("span");
            subtitleSpan.id = "analytics-subtitle";
            subtitleSpan.className = "subtitle";
            subtitleDiv.appendChild(subtitleSpan);
            analyticsContainer.appendChild(subtitleDiv);
        }
        subtitleSpan.textContent = subtitle;
        this.centerDivByTitle(subtitleDiv);
    }

    /**
     * Initializes or updates a metrics container within the specified container div.
     * This function creates a metrics display with a title, value, percentage change, 
     * and previous value, applying appropriate styles and icons based on the percentage change.
     *
     * @param {number} value - The current value to display in the metrics container.
     * @param {number} percentValue - The percentage change to display in the metrics container.
     * @param {number} previousValue - The previous value to display in the metrics container.
     */
    metricsInitializer(value, percentValue, previousValue, translation) {
        let analyticsContainer = this._shadowRoot.querySelector("div[id*='top-container']");
        let div = analyticsContainer.querySelector("div[id*='metrics-container']");
        if(!div){
            let centerDiv = analyticsContainer.querySelector("div[id*='metrics-center-div']");
            if(!centerDiv){
                centerDiv = document.createElement("div");
                centerDiv.id = "metrics-center-div";
                analyticsContainer.appendChild(centerDiv);
            }
            div = document.createElement("div");
            div.id = "metrics-container";
            div.className = "metrics-container";

            //value
            let valueDiv = document.createElement("div");
            valueDiv.id = "metrics-value-div";
            valueDiv.style = "align-items: baseline;";
            let valueSpan = document.createElement("span");
            valueSpan.id = "metrics-value";
            valueSpan.className = "metrics-value";

            let totalLabelSpan = document.createElement("span");
            totalLabelSpan.id = "metrics-total-label";
            totalLabelSpan.className = "metrics-total-label";

            valueDiv.appendChild(valueSpan);
            valueDiv.appendChild(totalLabelSpan);
            div.appendChild(valueDiv);

            //percentDiv
            let percentDiv = document.createElement("div");
            percentDiv.id = "metrics-percent-div";
            percentDiv.className = "div-align-center"

            //percentSpan
            let percentSpan = document.createElement("span");
            percentSpan.id = "metrics-percent-value";
            percentSpan.className = "metrics-percent";
            
            let iconSpan = document.createElement("i");
            iconSpan.id = "metrics-percent-icon";
            iconSpan.className = "icon";
            
            let span = document.createElement("span");
            span.id = "metrics-percent-background";
            span.className = "metrics-percent-background";
            
            percentDiv.appendChild(span);
            div.appendChild(percentDiv);
            
            //previousValue span
            let previousValueSpan = document.createElement("span");
            previousValueSpan.id = "metrics-previous-value";
            previousValueSpan.className = "metrics-percent padding-0-5-0-0";

            let previousValueText = document.createElement("span");
            previousValueText.id = "metrics-previous-value-text";
            previousValueText.className = "metrics-prev-value";
            previousValueText.textContent = translation['Compared to the previous period'] || 'Compared to the previous period';

            span.appendChild(previousValueSpan);
            span.appendChild(percentSpan);
            span.appendChild(iconSpan);
            span.appendChild(previousValueText);
            
            centerDiv.appendChild(div)
            analyticsContainer.appendChild(centerDiv);
        }

        let isDuration = this.hasAttribute("type") && this.getAttribute('type') == 'duration';
        let valueSpan = div.querySelector("span[id*='metrics-value']");
        let parsedNumber = this.numberParsing(value);
        valueSpan.innerHTML = parsedNumber;
        if (isDuration) {
            valueSpan.innerHTML += `<span class="value-small">s</span>`;
        }

        let totalLabelSpan = div.querySelector("span[id*='metrics-total-label']");
        let textContent = isDuration ? 'Average duration' : 'Total transits';
        textContent = translation[textContent] || textContent;
        totalLabelSpan.textContent = textContent;

        let margin = 0.5;
        let percentSpan = div.querySelector("span[id*='metrics-percent-value']");
        let percentBackground = div.querySelector("span[id*='metrics-percent-background']");
        let percentIcon = div.querySelector("i[id*='metrics-percent-icon']");
        let percentDiv = div.querySelector("div[id*='metrics-percent-div']");
        if(!percentValue && percentValue != 0){
            percentDiv.style = "display: none";
        }else{
            percentDiv.style = "";
            percentValue = Math.abs(Number(percentValue));
            if(percentValue.toString().includes('%')){
                percentSpan.textContent = `(${percentValue})`;
            }else{
                percentSpan.textContent = `(${percentValue.toFixed(2)}%)`;
            }
        }
        
        
        let previousValueSpan = div.querySelector("span[id*='metrics-previous-value']");
        let difference = value - previousValue;
        let parsedPrevNumber = this.numberParsing(difference, true);
        previousValueSpan.innerHTML = ((difference > 0)? '+' : '' )+  parsedPrevNumber
        if (isDuration) {
            previousValueSpan.innerHTML += `<span class="previous-value-small">s</span>`;
        }

        if ((isDuration && (difference) >= 0 && percentValue >= margin) 
            || (!isDuration && (difference) <= 0 &&  percentValue >= margin)) {
            percentIcon.className = "icon icon-trending_down";
            percentBackground.className = "trend-down metrics-percent-background";
        } else if ((isDuration && (difference) <= 0 && percentValue >= margin) 
            || (!isDuration && (difference) >= 0 && percentValue >= margin)) {
            percentIcon.className = "icon icon-trending_up";
            percentBackground.className = "trend-up metrics-percent-background";
        } else {
            percentBackground.className = "trend-neutral metrics-percent-background";
            percentIcon.className = "icon icon-trending_flat";
        }

        let metricsCenterDiv = this._shadowRoot.querySelector("div[id*='metrics-center-div']");
        this.centerDivByTitle(metricsCenterDiv);

        let metricsValueDiv = this._shadowRoot.querySelector("div[id*='metrics-value-div']");
        this.centerDivByTitle(metricsValueDiv);
    }

    numberParsing(value, isPrevValue, char = "'") {
        let val = Number(value);
        let formatted;
    
        if (Number.isInteger(val)) {
            formatted = val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, char);
        } else {
            formatted = val.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, char);
        }
    
        const escapedChar = char.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
    
        const regex = new RegExp(escapedChar, 'g');
        if(isPrevValue){
            return formatted;
        }
        else{
            return formatted.replace(regex, `<span class='special-char-small'>${char}</span>`);
        }
    }
}

customElements.define(
    "lane-analytics",
    LaneAnalyticsTransitChart
);